import * as AcceptNotification from '@ori-events/accept-notification';
import * as AcceptNotificationFailed from '@ori-events/accept-notification-failed';
import * as AddBackorderDemand from '@ori-events/add-backorder-demand';
import * as AddBackorderDemandFailed from '@ori-events/add-backorder-demand-failed';
import * as AddBasketOffer from '@ori-events/add-basket-offer';
import * as AddBasketOfferFailed from '@ori-events/add-basket-offer-failed';
import * as AddCatalogueOffer from '@ori-events/add-catalogue-offer';
import * as AddCatalogueOfferFailed from '@ori-events/add-catalogue-offer-failed';
import * as AddCustomerOrder from '@ori-events/add-customer-order';
import * as AddCustomerOrderFailed from '@ori-events/add-customer-order-failed';
import * as AddFreeShippingOffer from '@ori-events/add-free-shipping-offer';
import * as AddFreeShippingOfferFailed from '@ori-events/add-free-shipping-offer-failed';
import * as AddPersonalNutritionItems from '@ori-events/add-personal-nutrition-items';
import * as AddPersonalNutritionItemsFailed from '@ori-events/add-personal-nutrition-items-failed';
import * as AddToBasket from '@ori-events/add-to-basket';
import * as AddToBasketFailed from '@ori-events/add-to-basket-failed';
import * as AddVoucher from '@ori-events/add-voucher';
import * as AddVoucherFailed from '@ori-events/add-voucher-failed';
import * as CancelBackorderDemand from '@ori-events/cancel-backorder-demand';
import * as CancelSubscription from '@ori-events/cancel-subscription';
import * as CancelSubscriptionFailed from '@ori-events/cancel-subscription-failed';
import * as ClearBasket from '@ori-events/clear-basket';
import * as DelaySubscription from '@ori-events/delay-subscription';
import * as DelaySubscriptionFailed from '@ori-events/delay-subscription-failed';
import * as DeleteOfferItem from '@ori-events/delete-offer-item';
import * as DeleteOfferItemFailed from '@ori-events/delete-offer-item-failed';
import * as LoyaltyDialogResult from '@ori-events/loyalty-dialog-result';
import * as RemoveBasketItem from '@ori-events/remove-basket-item';
import * as RemoveBasketItemFailed from '@ori-events/remove-basket-item-failed';
import * as RemoveCustomerOrder from '@ori-events/remove-customer-order';
import * as RemoveCustomerOrderFailed from '@ori-events/remove-customer-order-failed';
import * as RemoveVoucher from '@ori-events/remove-voucher';
import * as RemoveVoucherFailed from '@ori-events/remove-voucher-failed';
import * as ReplaceShoppingBagItem from '@ori-events/replace-shopping-bag-item';
import * as ReplaceShoppingBagItemFailed from '@ori-events/replace-shopping-bag-item-failed';
import * as ReplaceSubscription from '@ori-events/replace-subscription';
import * as ReplaceSubscriptionFailed from '@ori-events/replace-subscription-failed';
import * as RequestShoppingContext from '@ori-events/request-shopping-context';
import * as ResetShippingSelection from '@ori-events/reset-shipping-selection';
import * as ShoppingContextLoaded from '@ori-events/shopping-context-loaded';
import * as UpdateBasketItem from '@ori-events/update-basket-item';
import * as UpdateBasketItemFailed from '@ori-events/update-basket-item-failed';
import * as UpdateCustomerOrderItem from '@ori-events/update-customer-order-item';
import * as UpdateCustomerOrderItemFailed from '@ori-events/update-customer-order-item-failed';
import * as UpdateDelivery from '@ori-events/update-delivery';
import * as UpdateDeliveryChannel from '@ori-events/update-delivery-channel';
import * as UpdateDeliveryChannelFailed from '@ori-events/update-delivery-channel-failed';
import * as UpdateDeliveryFailed from '@ori-events/update-delivery-failed';
import * as UpdateDonation from '@ori-events/update-donation';
import * as UpdateDonationFailed from '@ori-events/update-donation-failed';
import * as UpdateOfferItem from '@ori-events/update-offer-item';
import * as UpdateOfferItemFailed from '@ori-events/update-offer-item-failed';
import * as UpdateOfferItems from '@ori-events/update-offer-items';
import * as UpdatePaymentProvider from '@ori-events/update-payment-provider';
import * as UpdatePaymentProviderFailed from '@ori-events/update-payment-provider-failed';

import {
  acceptNotificationHandler,
  addBackorderDemandHandler,
  addBasketOfferHandler,
  addCatalogueOfferHandler,
  addCustomerOrderHandler,
  addFreeShippingOfferHandler,
  addPersonalNutritionItemsHandler,
  addToBasketHandler,
  addVoucherHandler,
  cancelBackorderDemandHandler,
  cancelSubscriptionHandler,
  checkLoyaltyDialogResultAndAddToBasketHandler,
  clearBasketHandler,
  delaySubscriptionHandler,
  deleteOfferItemHandler,
  removeBasketItemHandler,
  removeCustomerOrderHandler,
  removeVoucherHandler,
  replaceShoppingBagItemHandler,
  replaceSubscriptionHandler,
  requestShoppingContextHandler,
  resetShippingSelectionHandler,
  updateBasketItemHandler,
  updateCustomerOrderItemHandler,
  updateDeliveryHandler,
  updateDeliveryChannelHandler,
  updateDonationHandler,
  updateOfferItemHandler,
  updateOfferItemsHandler,
  updatePaymentProviderHandler,
} from '../handlers';
import type { ShoppingContextActionInfo, TriggerActionEventName } from '../types';

/**
 * Configuration of shopping context actions. Mapping of triggering events to response
 * success & failed events and event handlers for the responses.
 */
export const shoppingContextActions = new Map<TriggerActionEventName, ShoppingContextActionInfo>([
  [AcceptNotification.Name, { failedEvent: AcceptNotificationFailed.Name, handler: acceptNotificationHandler }],
  [AddBackorderDemand.Name, { failedEvent: AddBackorderDemandFailed.Name, handler: addBackorderDemandHandler }],
  [AddBasketOffer.Name, { failedEvent: AddBasketOfferFailed.Name, handler: addBasketOfferHandler }],
  [AddCatalogueOffer.Name, { failedEvent: AddCatalogueOfferFailed.Name, handler: addCatalogueOfferHandler }],
  [AddCustomerOrder.Name, { failedEvent: AddCustomerOrderFailed.Name, handler: addCustomerOrderHandler }],
  [AddFreeShippingOffer.Name, { failedEvent: AddFreeShippingOfferFailed.Name, handler: addFreeShippingOfferHandler }],
  [
    AddPersonalNutritionItems.Name,
    { failedEvent: AddPersonalNutritionItemsFailed.Name, handler: addPersonalNutritionItemsHandler },
  ],
  [AddToBasket.Name, { failedEvent: AddToBasketFailed.Name, handler: addToBasketHandler }],
  [AddVoucher.Name, { failedEvent: AddVoucherFailed.Name, handler: addVoucherHandler }],
  // TODO: no failed event available so far for CancelBackorderDemand
  [CancelBackorderDemand.Name, { handler: cancelBackorderDemandHandler }],
  [CancelSubscription.Name, { failedEvent: CancelSubscriptionFailed.Name, handler: cancelSubscriptionHandler }],
  // TODO: no failed event available so far for ClearBasket
  [ClearBasket.Name, { handler: clearBasketHandler }],
  [DelaySubscription.Name, { failedEvent: DelaySubscriptionFailed.Name, handler: delaySubscriptionHandler }],
  [DeleteOfferItem.Name, { failedEvent: DeleteOfferItemFailed.Name, handler: deleteOfferItemHandler }],
  // TODO: solve better the possible cases for LoyaltyDialogResult - maybe add-to-basket-failed,
  // and don't set up the loading state when just closed dialog (so exception to check for this case)
  [LoyaltyDialogResult.Name, { handler: checkLoyaltyDialogResultAndAddToBasketHandler }],
  [RemoveBasketItem.Name, { failedEvent: RemoveBasketItemFailed.Name, handler: removeBasketItemHandler }],
  [RemoveCustomerOrder.Name, { failedEvent: RemoveCustomerOrderFailed.Name, handler: removeCustomerOrderHandler }],
  [RemoveVoucher.Name, { failedEvent: RemoveVoucherFailed.Name, handler: removeVoucherHandler }],
  [
    ReplaceShoppingBagItem.Name,
    { failedEvent: ReplaceShoppingBagItemFailed.Name, handler: replaceShoppingBagItemHandler },
  ],
  [ReplaceSubscription.Name, { failedEvent: ReplaceSubscriptionFailed.Name, handler: replaceSubscriptionHandler }],
  [RequestShoppingContext.Name, { successEvent: ShoppingContextLoaded.Name, handler: requestShoppingContextHandler }],
  // TODO: no failed event available so far for ResetShippingSelection
  [ResetShippingSelection.Name, { handler: resetShippingSelectionHandler }],
  [UpdateBasketItem.Name, { failedEvent: UpdateBasketItemFailed.Name, handler: updateBasketItemHandler }],
  [
    UpdateCustomerOrderItem.Name,
    { failedEvent: UpdateCustomerOrderItemFailed.Name, handler: updateCustomerOrderItemHandler },
  ],
  [UpdateDelivery.Name, { failedEvent: UpdateDeliveryFailed.Name, handler: updateDeliveryHandler }],
  [
    UpdateDeliveryChannel.Name,
    { failedEvent: UpdateDeliveryChannelFailed.Name, handler: updateDeliveryChannelHandler },
  ],
  [UpdateDonation.Name, { failedEvent: UpdateDonationFailed.Name, handler: updateDonationHandler }],
  [UpdateOfferItem.Name, { failedEvent: UpdateOfferItemFailed.Name, handler: updateOfferItemHandler }],
  // TODO: no failed event available so far for UpdateOfferItems
  [UpdateOfferItems.Name, { handler: updateOfferItemsHandler }],
  [
    UpdatePaymentProvider.Name,
    { failedEvent: UpdatePaymentProviderFailed.Name, handler: updatePaymentProviderHandler },
  ],
]);
